import { ReactiveVar } from '@apollo/client';

import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import { notificationsVar } from '../../cache/cache';
import {
  emptyRecommendedGoalsBySpecializedMajorsCache,
  IRecommendedGoalsBySpecializedMajorsCache,
  IRecommendedGoalsBySpecializedMajors,
  RecommendedGoalsBySpecializedMajorsReactiveVar,
} from '../../cache/learner/recommendedGoalsBySpecializedMajorsReactiveVar';
import { decryptor, encryptor } from '../../utils/decryptor';

interface IRecommendedGoalsBySpecializedMajorsHook {
  clearRecommendedGoalsBySpecializedMajorsError: () => void;
  setRecommendedGoalsBySpecializedMajorsError: (errorMessage: string) => void;
  setRecommendedGoalsBySpecializedMajors: (recommendedGoals: IRecommendedGoalsBySpecializedMajors[]) => void;
  setFromLocalRecommendedGoalsBySpecializedMajors: () => boolean;
  updateRecommendedGoalsBySpecializedMajors: (id: string) => void;
}

const RecommendedGoalsBySpecializedMajorsHook = (
  recommendedGoalsBySpecializedMajorsVarProp: ReactiveVar<IRecommendedGoalsBySpecializedMajorsCache>,
): IRecommendedGoalsBySpecializedMajorsHook => {
  const { data: recommendedGoalsBySpecializedMajorsData, error: recommendedGoalsBySpecializedMajorsError } =
    recommendedGoalsBySpecializedMajorsVarProp();

  const clearRecommendedGoalsBySpecializedMajorsError = (): void => {
    RecommendedGoalsBySpecializedMajorsReactiveVar(emptyRecommendedGoalsBySpecializedMajorsCache);
  };

  const setRecommendedGoalsBySpecializedMajorsError = (errorMessage: string): void => {
    RecommendedGoalsBySpecializedMajorsReactiveVar({
      ...emptyRecommendedGoalsBySpecializedMajorsCache,
      error: errorMessage,
    });
  };

  const setRecommendedGoalsBySpecializedMajors = (recommendedGoals: IRecommendedGoalsBySpecializedMajors[]): void => {
    RecommendedGoalsBySpecializedMajorsReactiveVar({
      ...emptyRecommendedGoalsBySpecializedMajorsCache,
      data: recommendedGoals,
    });
    const encryptedData = encryptor(JSON.stringify(recommendedGoals));

    if (!encryptedData) {
      return;
    }

    sessionStorage.setItem('recommendedGoalsBySpecializedMajors', encryptedData);
  };

  const setFromLocalRecommendedGoalsBySpecializedMajors = (): boolean => {
    const dataLocal = sessionStorage.getItem('recommendedGoalsBySpecializedMajors');
    const decryptedData = decryptor(dataLocal ?? '');

    if (!decryptedData) {
      return false;
    }

    RecommendedGoalsBySpecializedMajorsReactiveVar({ error: '', data: JSON.parse(decryptedData) });

    return true;
  };

  const updateRecommendedGoalsBySpecializedMajors = (id: string): void => {
    if (recommendedGoalsBySpecializedMajorsError) {
      notificationsVar([
        {
          id: 'updateRecommendedGoalsBySpecializedMajors',
          title: recommendedGoalsBySpecializedMajorsError,
          description: recommendedGoalsBySpecializedMajorsError,
          autoClose: true,
          leftIcon: <WarningAmberRoundedIcon color="error" />,
        },
      ]);
    } else if (recommendedGoalsBySpecializedMajorsData) {
      setRecommendedGoalsBySpecializedMajors(
        recommendedGoalsBySpecializedMajorsData.map((goal) => ({
          academicProgramId: goal.academicProgramId,
          academicProgramName: goal.academicProgramName,
          match: goal.match.filter((occupation) => occupation.jobTitleId !== id),
        })),
      );
    }
  };

  return {
    clearRecommendedGoalsBySpecializedMajorsError,
    setRecommendedGoalsBySpecializedMajorsError,
    setRecommendedGoalsBySpecializedMajors,
    setFromLocalRecommendedGoalsBySpecializedMajors,
    updateRecommendedGoalsBySpecializedMajors,
  };
};

export default RecommendedGoalsBySpecializedMajorsHook;
